import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { generateOTP, validateOTP } from '../api/auth.api';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const BRAND = '#082189';
const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const ccFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1);    }
`;
const ccSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);    }
`;
const ccShake = keyframes`
  0%,100% { transform: translateX(0);  }
  20%,60%  { transform: translateX(-6px); }
  40%,80%  { transform: translateX(6px);  }
`;
const ccSuccessPop = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;
const ccSpinner = keyframes`
  to { transform: rotate(360deg); }
`;

/* ─────────────────────────────────────────────
   Styled components
───────────────────────────────────────────── */
const SpinnerEl = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${ccSpinner} 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #b91c1c;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  animation: ${ccSlideUp} 0.2s ease;
`;

const ErrorBang = styled.span`
  font-size: 15px;
  line-height: 1.3;
`;

const StepWrap = styled.div`
  animation: ${ccSlideUp} 0.25s ease both;
`;

const LogoWrap = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LogoBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -1px;
  box-shadow: 0 8px 24px rgba(8, 33, 137, 0.28);
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  display: block;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OptionalTag = styled.span`
  color: #9ca3af;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 0;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
`;

const PhonePrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 14px;
  background: #f9fafb;
  border-right: 2px solid #e5e7eb;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  user-select: none;
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  color: #111827;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: ${BRAND};
    box-shadow: 0 0 0 3px rgba(8, 33, 137, 0.12);
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const PhoneInput = styled(InputField)`
  border: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1;

  &:focus {
    border: none;
    box-shadow: none;
  }
`;

const ExpandGroup = styled.div`
  margin-bottom: 20px;
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${BRAND};
  text-decoration: none;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.75;
  }
`;

const ExpandCaret = styled.span`
  display: inline-block;
  transition: transform 0.2s;
  transform: ${(p) => (p.$expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  font-size: 12px;
`;

const NewUserFields = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${ccSlideUp} 0.2s ease;
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 14px;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s, transform 0.12s;
  cursor: pointer;
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 100%);
  box-shadow: 0 4px 16px rgba(8, 33, 137, 0.3);

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: scale(1.01);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const VerifyBtn = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s, opacity 0.15s, transform 0.12s;

  ${(p) =>
    p.$active
      ? css`
          background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 100%);
          color: #fff;
          box-shadow: 0 4px 16px rgba(8, 33, 137, 0.3);
        `
      : css`
          background: #e5e7eb;
          color: #9ca3af;
          box-shadow: none;
        `}

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: scale(1.01);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const TermsText = styled.p`
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 20px;
  margin-bottom: 0;
`;

const TermsLink = styled.a`
  color: ${BRAND};
  text-decoration: none;
  font-weight: 500;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  padding: 0;
  margin-bottom: 20px;
  transition: color 0.15s;

  &:hover {
    color: #111827;
  }
`;

const OTPHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const OTPIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f0f3ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const OTPTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
`;

const OTPSub = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const OTPSubStrong = styled.span`
  font-weight: 600;
  color: #374151;
`;

const OTPBoxRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
  animation: ${(p) => (p.$shake ? css`${ccShake} 0.4s ease` : 'none')};

  @media (max-width: 400px) {
    gap: 6px;
  }
`;

const OTPInput = styled.input`
  width: 46px;
  height: 54px !important;
  /* !important defeats the vendor rule input[type="tel"]{padding:26px}, whose
     attribute-selector specificity otherwise beats this styled class and pushes
     the typed digit clean out of the box (invisible but still submitted). */
  text-align: center !important;
  font-size: 22px;
  font-weight: 700;
  line-height: 54px !important;
  color: #111827 !important;
  -webkit-text-fill-color: #111827;
  border-radius: 10px;
  outline: none;
  padding: 0 !important;
  margin: 0;
  box-shadow: none;
  caret-color: ${BRAND};
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s;
  -moz-appearance: textfield;
  appearance: textfield;

  background: ${(p) => (p.$filled ? '#f0f3ff' : '#fff')};
  border: 2px solid
    ${(p) => (p.$error ? '#ef4444' : p.$filled ? BRAND : '#e5e7eb')};

  @media (max-width: 480px) {
    width: 42px;
    height: 50px;
    font-size: 20px;
  }

  @media (max-width: 400px) {
    width: 100%;
    min-width: 0;
    height: 48px;
    font-size: 18px;
    line-height: 48px;
  }
`;

const ResendWrap = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const ResendText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const TimerStrong = styled.span`
  font-weight: 700;
  color: ${BRAND};
`;

const ResendBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s;
  color: ${BRAND};

  &:disabled {
    cursor: default;
  }
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  animation: ${ccSlideUp} 0.3s ease both;
`;

const SuccessCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  animation: ${ccSuccessPop} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;

const SuccessTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
`;

const SuccessSub = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
`;

const SuccessName = styled.span`
  font-weight: 700;
  color: #0f172a;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${ccFadeIn} 0.2s ease both;

  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 36px;
  width: 100%;
  max-width: 420px;
  position: relative;
  animation: ${ccFadeIn} 0.25s ease both;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);

  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 20px 20px 0 0;
    max-width: 100%;
    max-height: 94vh;
    overflow-y: auto;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6b7280;
  line-height: 1;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

/* ─────────────────────────────────────────────
   Small utility components
───────────────────────────────────────────── */
function Spinner() {
  return <SpinnerEl />;
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <ErrorBox>
      <ErrorBang>!</ErrorBang>
      <span>{message}</span>
    </ErrorBox>
  );
}

/* ─────────────────────────────────────────────
   Step 1 – Mobile number entry
───────────────────────────────────────────── */
function StepPhone({ onSuccess }) {
  const [mobile, setMobile]       = useState('');
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [expanded, setExpanded]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(val);
    setError('');
  };

  const handleSend = async () => {
    if (mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await generateOTP({ mobile, name, email });
      // API success check — tolerate various response shapes
      if (data?.isSuccess === false || data?.status === 'error') {
        throw new Error(data?.message || data?.title || 'Failed to send OTP. Please try again.');
      }
      onSuccess({ mobile, name, email });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) handleSend();
  };

  return (
    <StepWrap>
      {/* Logo */}
      <LogoWrap>
        <LogoBadge>CC</LogoBadge>
        <Title>Welcome to CityCraft</Title>
        <Subtitle>Enter your mobile number to continue</Subtitle>
      </LogoWrap>

      {/* Mobile input */}
      <FieldGroup>
        <Label>Mobile Number</Label>
        <PhoneRow
          onFocusCapture={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8,33,137,0.12)'; }}
          onBlurCapture={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <PhonePrefix>+91</PhonePrefix>
          <PhoneInput
            type="tel"
            inputMode="numeric"
            placeholder="98765 43210"
            value={mobile}
            onChange={handleMobileChange}
            onKeyDown={handleKeyDown}
            maxLength={10}
            autoFocus
          />
        </PhoneRow>
      </FieldGroup>

      {/* New user collapsible */}
      <ExpandGroup>
        <ExpandToggle onClick={() => setExpanded(v => !v)} type="button">
          <ExpandCaret $expanded={expanded}>▶</ExpandCaret>
          New user? Add your details
        </ExpandToggle>

        {expanded && (
          <NewUserFields>
            <div>
              <Label>
                Full Name <OptionalTag>(optional)</OptionalTag>
              </Label>
              <InputField
                type="text"
                placeholder="Anurag Sharma"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div>
              <Label>
                Email <OptionalTag>(optional)</OptionalTag>
              </Label>
              <InputField
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
          </NewUserFields>
        )}
      </ExpandGroup>

      {/* CTA */}
      <SendBtn
        onClick={handleSend}
        disabled={loading || mobile.length !== 10}
      >
        {loading && <Spinner />}
        {loading ? 'Sending OTP…' : 'Send OTP →'}
      </SendBtn>

      <ErrorBanner message={error} />

      <TermsText>
        By continuing, you agree to our{' '}
        <TermsLink href="/about">Terms</TermsLink>{' '}
        &amp;{' '}
        <TermsLink href="/about">Privacy Policy</TermsLink>
      </TermsText>
    </StepWrap>
  );
}

/* ─────────────────────────────────────────────
   Step 2 – OTP verification
───────────────────────────────────────────── */
function StepOTP({ mobile, name, email, onBack, onSuccess }) {
  const [digits, setDigits]         = useState(Array(OTP_LENGTH).fill(''));
  const [shakeError, setShakeError] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [timer, setTimer]           = useState(RESEND_SECONDS);
  const [resending, setResending]   = useState(false);
  const inputRefs = useRef([]);

  // Start countdown
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const triggerShake = useCallback(() => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 500);
  }, []);

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    if (!digit && value !== '') return; // non-numeric, ignore

    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError('');

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        setDigits(next);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    setError('');
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setDigits(Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    try {
      const data = await generateOTP({ mobile, name, email });
      if (data?.isSuccess === false || data?.status === 'error') {
        throw new Error(data?.message || 'Failed to resend OTP.');
      }
      setTimer(RESEND_SECONDS);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    const otpCode = digits.join('');
    if (otpCode.length < OTP_LENGTH) {
      triggerShake();
      setError('Please enter all 6 digits.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await validateOTP({ mobile, otpCode, name, email });
      // Explicit failure signals from the envelope.
      const failed =
        data?.isSuccess === false ||
        data?.status === 'error' ||
        data?.responseStatus === 0 ||
        data?.responseStatus === false;
      if (failed) {
        throw new Error(data?.message || data?.title || 'Invalid OTP. Please try again.');
      }
      // Pass the FULL envelope so the top-level token (sibling of `result`)
      // isn't lost — AuthContext.login extracts token + user from it.
      onSuccess(data);
    } catch (err) {
      triggerShake();
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const allFilled = digits.every(d => d !== '');

  return (
    <StepWrap>
      <BackBtn onClick={onBack} type="button">
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back
      </BackBtn>

      <OTPHeader>
        <OTPIconCircle>
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l1.14-1.14a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </OTPIconCircle>
        <OTPTitle>Enter Verification Code</OTPTitle>
        <OTPSub>
          We sent a 6-digit code to{' '}
          <OTPSubStrong>+91 {mobile}</OTPSubStrong>
        </OTPSub>
      </OTPHeader>

      {/* OTP Boxes */}
      <OTPBoxRow $shake={shakeError}>
        {digits.map((digit, index) => (
          <OTPInput
            key={index}
            ref={el => (inputRefs.current[index] = el)}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={digit}
            onChange={e => handleDigitChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={e => e.target.select()}
            maxLength={1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            $filled={!!digit}
            $error={shakeError}
          />
        ))}
      </OTPBoxRow>

      {/* Resend */}
      <ResendWrap>
        {timer > 0 ? (
          <ResendText>
            Resend code in{' '}
            <TimerStrong>
              {String(Math.floor(timer / 60)).padStart(2, '0')}:{String(timer % 60).padStart(2, '0')}
            </TimerStrong>
          </ResendText>
        ) : (
          <ResendText>
            Didn't receive it?{' '}
            <ResendBtn onClick={handleResend} disabled={resending}>
              {resending ? 'Sending…' : 'Resend OTP'}
            </ResendBtn>
          </ResendText>
        )}
      </ResendWrap>

      {/* CTA */}
      <VerifyBtn
        onClick={handleVerify}
        disabled={loading || !allFilled}
        $active={allFilled}
      >
        {loading && <Spinner />}
        {loading ? 'Verifying…' : 'Verify OTP →'}
      </VerifyBtn>

      <ErrorBanner message={error} />
    </StepWrap>
  );
}

/* ─────────────────────────────────────────────
   Success overlay
───────────────────────────────────────────── */
function SuccessFlash({ name }) {
  return (
    <SuccessWrap>
      <SuccessCircle>
        <svg width={38} height={38} viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </SuccessCircle>
      <SuccessTitle>Login Successful!</SuccessTitle>
      <SuccessSub>
        Welcome back,{' '}
        <SuccessName>{name || 'there'}</SuccessName>
        !
      </SuccessSub>
    </SuccessWrap>
  );
}

/* ─────────────────────────────────────────────
   LoginModal — main export
───────────────────────────────────────────── */
const LoginModal = () => {
  const { showLogin, closeLogin, login } = useAuth();
  const [step, setStep]             = useState('phone'); // 'phone' | 'otp' | 'success'
  const [phoneData, setPhoneData]   = useState({ mobile: '', name: '', email: '' });

  // Reset to phone step when modal is opened fresh
  useEffect(() => {
    if (showLogin) {
      setStep('phone');
      setPhoneData({ mobile: '', name: '', email: '' });
    }
  }, [showLogin]);

  if (!showLogin) return null;

  const handlePhoneSuccess = ({ mobile, name, email }) => {
    setPhoneData({ mobile, name, email });
    setStep('otp');
  };

  const handleOTPSuccess = (result) => {
    setStep('success');
    // Give user 1.2s to see the success flash, then commit login
    setTimeout(() => {
      login(result);
    }, 1200);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeLogin();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label="Login">
      <ModalCard>
        {step !== 'success' && (
          <CloseBtn onClick={closeLogin} aria-label="Close" type="button">
            ×
          </CloseBtn>
        )}

        {step === 'phone' && (
          <StepPhone onSuccess={handlePhoneSuccess} />
        )}

        {step === 'otp' && (
          <StepOTP
            mobile={phoneData.mobile}
            name={phoneData.name}
            email={phoneData.email}
            onBack={() => setStep('phone')}
            onSuccess={handleOTPSuccess}
          />
        )}

        {step === 'success' && (
          <SuccessFlash name={phoneData.name} />
        )}
      </ModalCard>
    </ModalOverlay>
  );
};

export default LoginModal;
